import React, {useState} from 'react';
import {
  Modal,
  View,
  StyleSheet,
  Alert,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCTextInput from '@/Atoms/CCTextInput';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import ChessRestClient from '@/Api/ChessRestClient';

interface CheatingReportModalProps {
  visible: boolean;
  onClose: () => void;
  gameUuid: string;
  reportedFor: string; // username of the player being reported
  playerDisplayName?: string; // display name for UI
}

const CheatingReportModal: React.FC<CheatingReportModalProps> = ({
  visible,
  onClose,
  gameUuid,
  reportedFor,
  playerDisplayName,
}) => {
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!gameUuid || !reportedFor) {
      Alert.alert('Error', 'Missing required information to submit report');
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Submitting cheating report:', {
        game_uuid: gameUuid,
        reported_for: reportedFor,
        description: description.trim() || undefined,
      });

      const response = await ChessRestClient.post(
        'report_cheating_suspicion/',
        {
          game_uuid: gameUuid,
          reported_for: reportedFor,
          description: description.trim() || undefined,
        },
      );

      console.log('Cheating report success response:', response);

      if (response.data && response.data.report_id) {
        Alert.alert(
          'Report Submitted',
          `Your cheating report has been submitted successfully. Report ID: ${response.data.report_id}`,
          [
            {
              text: 'OK',
              onPress: () => {
                setDescription('');
                onClose();
              },
            },
          ],
        );
      } else {
        Alert.alert(
          'Report Submitted',
          'Your cheating report has been submitted successfully.',
          [
            {
              text: 'OK',
              onPress: () => {
                setDescription('');
                onClose();
              },
            },
          ],
        );
      }
      handleClose();
    } catch (error: any) {
      console.error('Cheating report error:', error);

      let errorMessage = 'Failed to submit report. Please try again.';

      if (error?.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);

        if (error.response.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (error.response.status === 400) {
          errorMessage = 'Invalid request. Please check the game information.';
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      Alert.alert('Error', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setDescription('');
      onClose();
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}>
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <CCText style={styles.title} fontSize={FontSizes[16]}>
              Report Cheating
            </CCText>
            <TouchableOpacity
              onPress={handleClose}
              style={styles.closeButton}
              disabled={isSubmitting}>
              <CCText style={styles.closeButtonText} fontSize={FontSizes[18]}>
                ×
              </CCText>
            </TouchableOpacity>
          </View>

          <View style={styles.content}>
            <CCText style={styles.reportingText} fontSize={FontSizes[14]}>
              Reporting player:{' '}
              <CCText style={styles.playerName} fontSize={FontSizes[14]}>
                {playerDisplayName || reportedFor}
              </CCText>
            </CCText>

            <CCText style={styles.label} fontSize={FontSizes[12]}>
              Description (optional):
            </CCText>

            <CCTextInput
              value={description}
              onChangeText={setDescription}
              placeholder="Describe the suspected cheating behavior..."
              multiline
              numberOfLines={4}
              maxLength={500}
              style={styles.textInput}
              editable={!isSubmitting}
            />

            <CCText style={styles.charCount} fontSize={FontSizes[10]}>
              {description.length}/500
            </CCText>
          </View>

          <View style={styles.footer}>
            <CCButton
              onPress={handleClose}
              style={[styles.button, styles.cancelButton]}
              disabled={isSubmitting}>
              <CCText color={CCColors.Primary.Brown} fontSize={FontSizes[12]}>
                Cancel
              </CCText>
            </CCButton>
            <CCButton
              onPress={handleSubmit}
              style={[styles.button, styles.submitButton]}
              disabled={isSubmitting}>
              <CCText style={styles.buttonText} fontSize={FontSizes[12]}>
                {isSubmitting ? 'Submitting...' : 'Submit Report'}
              </CCText>
            </CCButton>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  buttonText: {
    color: CCColors.White,
    fontWeight: 'bold',
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: CCColors.White,
    borderRadius: 12,
    width: '100%',
    maxWidth: 400,
    maxHeight: '80%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: CCColors.BorderLight,
  },
  title: {
    fontWeight: 'bold',
    color: CCColors.Black,
  },
  closeButton: {
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    color: CCColors.DarkGrey,
    fontWeight: 'bold',
  },
  content: {
    padding: 20,
  },
  reportingText: {
    marginBottom: 15,
    color: CCColors.Black,
  },
  playerName: {
    fontWeight: 'bold',
    color: CCColors.Primary.Brown,
  },
  label: {
    marginBottom: 8,
    color: CCColors.DarkGrey,
    fontWeight: '500',
  },
  textInput: {
    borderWidth: 1,
    borderColor: CCColors.BorderLight,
    borderRadius: 8,
    padding: 12,
    minHeight: 80,
    textAlignVertical: 'top',
    marginBottom: 5,
  },
  charCount: {
    textAlign: 'right',
    color: CCColors.DarkGrey,
    marginBottom: 15,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 20,
    paddingTop: 10,
    gap: 10,
  },
  button: {
    // flex: 1,
    paddingVertical: 12,
  },
  cancelButton: {
    backgroundColor: CCColors.BorderLight,
  },
  submitButton: {
    backgroundColor: CCColors.Primary.Brown,
  },
});

export default CheatingReportModal;
